import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '../components/DataTable';
import AsignarActivoModal from '../components/AsignarActivoModal';
import HistorialAsignacionesModal from '../components/HistorialAsignacionesModal';
import { formatUbicacion } from '../utils/locationUtils';
import EmpresaBadge from '../components/EmpresaBadge';

import { HardDrive, Smartphone, Monitor, List, Trash2, AlertTriangle, Package, Edit, Archive, FileBarChart } from 'lucide-react';
import ReporteInventarioModal from '../components/ReporteInventarioModal';
import { useAuth } from '../contexts/AuthContext';
import { Wifi, User, History, Plus, Eye } from '../components/icons';
import Consumibles from './Consumibles';
import ActivosForm from './ActivosForm';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { activosAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

const columnHelper = createColumnHelper()

// Normaliza valores de almacenamiento inconsistentes del backend
// "GB 480" → "480 GB" | "SSD 480" → "SSD 480 GB" | "480 GB SSD" → sin cambio
const formatStorageDisplay = (raw) => {
    if (!raw || typeof raw !== 'string') return raw;
    const s = raw.trim();
    // Detectar patrón "UNIT NUMBER" (ej: "GB 480", "TB 2")
    const unitFirst = s.match(/^(GB|TB|MB)\s+(\d+)$/i);
    if (unitFirst) return `${unitFirst[2]} ${unitFirst[1].toUpperCase()}`;
    // Detectar patrón "TYPE NUMBER" sin unidad (ej: "SSD 480", "HDD 1000")
    const typeFirst = s.match(/^(SSD|HDD|NVMe|M\.2)\s+(\d+)$/i);
    if (typeFirst) return `${typeFirst[2]} GB ${typeFirst[1].toUpperCase()}`;
    return s;
};

const renderCodigoCell = ({ getValue, row }) => (
    <div className="flex items-center gap-2">
        <span className="font-mono font-semibold">{getValue()}</span>
        <EmpresaBadge empresa={row.original.empresa} />
    </div>
);

// Columnas por categoría
const columnsByCategory = {
    all: [
        columnHelper.accessor('codigo', { header: 'Código', cell: renderCodigoCell }),
        columnHelper.accessor('categoria', { header: 'Categoría' }),
        columnHelper.accessor('estado', { header: 'Estado' }),
        columnHelper.accessor(row => formatUbicacion(row.ubicacion), { id: 'ubicacion', header: 'Ubicación' }),
        columnHelper.accessor(
            row => row.asignadoA ? `${row.asignadoA.nombre} ${row.asignadoA.apellido || ''} (${row.asignadoA.departamento})`.replace(/\s+/g, ' ').trim() : 'Sin asignar',
            {
                id: 'asignadoA',
                header: 'Asignado a',
                enableSorting: false,
                enableColumnFilter: false,
            }
        ),
    ],
    Equipos: [
        columnHelper.accessor('codigo', { header: 'Código', cell: renderCodigoCell }),
        columnHelper.accessor('tipoEquipo', { header: 'Tipo Equipo' }),
        columnHelper.accessor('marca', { header: 'Marca' }),
        columnHelper.accessor('procesador', { header: 'Procesador' }),
        columnHelper.accessor('ram', { header: 'RAM' }),
        columnHelper.accessor(
            row => {
                // 0) Campos de texto ya calculados desde backend
                const direct = row.almacenamiento || row.Almacenamiento || row.storage || row.Storage;
                if (typeof direct === 'string' && direct.trim().length > 0) return formatStorageDisplay(direct);

                // Soportar nombres de campo en distintas mayúsculas y formatos
                const rawJson = row.discosJson || row.DiscosJson || row.discosJSON;
                // 1) Preferir JSON serializado desde BD
                if (rawJson && typeof rawJson === 'string') {
                    try {
                        const discos = JSON.parse(rawJson);
                        if (Array.isArray(discos)) {
                            return discos.map(d => {
                                const tipo = d.tipo || d.Tipo || '';
                                const capacidad = d.capacidad || d.Capacidad || '';
                                const sufijo = typeof capacidad === 'number' ? `${capacidad}GB` : capacidad;
                                return formatStorageDisplay(`${tipo} ${sufijo}`.trim());
                            }).filter(Boolean).join(', ');
                        }
                    } catch (e) {
                        console.warn('No se pudo parsear DiscosJson:', e);
                    }
                }
                // 2) Si viene como arreglo ya procesado
                if (Array.isArray(row.discos)) {
                    return row.discos.map(d => {
                        const tipo = d.tipo || d.Tipo || '';
                        const capacidad = d.capacidad || d.Capacidad || '';
                        const sufijo = typeof capacidad === 'number' ? `${capacidad}GB` : capacidad;
                        return formatStorageDisplay(`${tipo} ${sufijo}`.trim());
                    }).filter(Boolean).join(', ');
                }
                // 3) Otros posibles nombres
                if (Array.isArray(row.Discos)) {
                    return row.Discos.map(d => {
                        const tipo = d.tipo || d.Tipo || '';
                        const capacidad = d.capacidad || d.Capacidad || '';
                        const sufijo = typeof capacidad === 'number' ? `${capacidad}GB` : capacidad;
                        return formatStorageDisplay(`${tipo} ${sufijo}`.trim());
                    }).filter(Boolean).join(', ');
                }
                return '';
            },
            { header: 'Almacenamiento' }
        ),
        columnHelper.accessor(row => formatUbicacion(row.ubicacion), { id: 'ubicacion', header: 'Ubicación' }),
        columnHelper.accessor(
            row => row.asignadoA ? `${row.asignadoA.nombre} ${row.asignadoA.apellido || ''} (${row.asignadoA.departamento})`.replace(/\s+/g, ' ').trim() : 'Sin asignar',
            {
                id: 'asignadoA',
                header: 'Asignado a',
                enableSorting: false,
                enableColumnFilter: false,
            }
        ),
    ],
    Móviles: [
        columnHelper.accessor('codigo', { header: 'Código', cell: renderCodigoCell }),
        columnHelper.accessor('marca', { header: 'Marca' }),
        columnHelper.accessor('modelo', { header: 'Modelo' }),
        columnHelper.accessor('imei', { header: 'IMEI' }),
        // El campo real en el modelo es "numeroCelular", no "celular" -- por
        // eso esta columna aparecia siempre vacia.
        columnHelper.accessor('numeroCelular', { header: 'N° Celular' }),
        columnHelper.accessor(row => formatUbicacion(row.ubicacion), { id: 'ubicacion', header: 'Ubicación' }),
        columnHelper.accessor(
            row => row.asignadoA ? `${row.asignadoA.nombre} ${row.asignadoA.apellido || ''} (${row.asignadoA.departamento})`.replace(/\s+/g, ' ').trim() : 'Sin asignar',
            {
                id: 'asignadoA',
                header: 'Asignado a',
                enableSorting: false,
                enableColumnFilter: false,
            }
        ),
    ],
    Monitores: [
        columnHelper.accessor('codigo', { header: 'Código', cell: renderCodigoCell }),
        columnHelper.accessor('nombre', { header: 'Nombre' }),
        columnHelper.accessor('serie', { header: 'N° Serie' }),
        columnHelper.accessor('marca', { header: 'Marca' }),
        columnHelper.accessor('pulgadas', { header: 'Pulgadas' }),
        columnHelper.accessor(row => formatUbicacion(row.ubicacion), { id: 'ubicacion', header: 'Ubicación' }),
        columnHelper.accessor(
            row => row.asignadoA ? `${row.asignadoA.nombre} ${row.asignadoA.apellido || ''} (${row.asignadoA.departamento})`.replace(/\s+/g, ' ').trim() : 'Sin asignar',
            {
                id: 'asignadoA',
                header: 'Asignado a',
                enableSorting: false,
                enableColumnFilter: false,
            }
        ),
    ],
    Periféricos: [
        columnHelper.accessor('codigo', { header: 'Código', cell: renderCodigoCell }),
        columnHelper.accessor('nombre', { header: 'Nombre' }),
        columnHelper.accessor('cantidad', { header: 'Cantidad' }),
        columnHelper.accessor(row => formatUbicacion(row.ubicacion), { id: 'ubicacion', header: 'Ubicación' }),
        columnHelper.accessor(
            row => row.asignadoA ? `${row.asignadoA.nombre} ${row.asignadoA.apellido || ''} (${row.asignadoA.departamento})`.replace(/\s+/g, ' ').trim() : 'Sin asignar',
            {
                id: 'asignadoA',
                header: 'Asignado a',
                enableSorting: false,
                enableColumnFilter: false,
            }
        ),
    ],
    Accesorios: [
        columnHelper.accessor('codigo', { header: 'Código', cell: renderCodigoCell }),
        columnHelper.accessor('nombre', { header: 'Nombre' }),
        columnHelper.accessor('cantidad', { header: 'Cantidad' }),
        columnHelper.accessor(row => formatUbicacion(row.ubicacion), { id: 'ubicacion', header: 'Ubicación' }),
        columnHelper.accessor(
            row => row.asignadoA ? `${row.asignadoA.nombre} ${row.asignadoA.apellido || ''} (${row.asignadoA.departamento})`.replace(/\s+/g, ' ').trim() : 'Sin asignar',
            {
                id: 'asignadoA',
                header: 'Asignado a',
                enableSorting: false,
                enableColumnFilter: false,
            }
        ),
    ],
    Red: [
        columnHelper.accessor('codigo', { header: 'Código', cell: renderCodigoCell }),
        columnHelper.accessor('nombre', { header: 'Nombre' }),
        columnHelper.accessor('cantidad', { header: 'Cantidad' }),
        columnHelper.accessor(row => formatUbicacion(row.ubicacion), { id: 'ubicacion', header: 'Ubicación' }),
        columnHelper.accessor(
            row => row.asignadoA ? `${row.asignadoA.nombre} ${row.asignadoA.apellido || ''} (${row.asignadoA.departamento})`.replace(/\s+/g, ' ').trim() : 'Sin asignar',
            {
                id: 'asignadoA',
                header: 'Asignado a',
                enableSorting: false,
                enableColumnFilter: false,
            }
        ),
    ],
}

// A-7: module-level constant — never recreated on re-render
const EQUIPOS_CATEGORIAS = ['Equipos', 'Notebook', 'Laptop', 'Desktop', 'PC', 'Computador', 'Equipo']

// Categorías fijas (comentadas por ahora)
// const categories = [
//     { key: 'all', label: 'Todos', icon: List },
//     { key: 'Equipos', label: 'Equipos', icon: HardDrive },
//     { key: 'Móviles', label: 'Móviles', icon: Smartphone },
//     { key: 'Monitores', label: 'Monitores', icon: Monitor },
//     { key: 'Periféricos', label: 'Periféricos', icon: Keyboard },
//     { key: 'Accesorios', label: 'Accesorios', icon: Usb },
//     { key: 'Red', label: 'Red', icon: Wifi },
// ]

export default function Activos() {
    const navigate = useNavigate()
    const location = useLocation()
    const { showToast } = useToast()
    const { user: authUser } = useAuth()
    const [activos, setActivos] = useState([])
    const [serverPage, setServerPage] = useState(1)
    const [serverPagination, setServerPagination] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 1 })
    const [serverStats, setServerStats] = useState(null)
    const [loading, setLoading] = useState(true)
    // Solo la carguísima carga inicial debe reemplazar toda la página con el skeleton --
    // un refetch por búsqueda/filtro/paginación no debe desmontar el DataTable (perdería
    // lo que el usuario ya escribió en su cuadro de búsqueda).
    const hasLoadedOnce = useRef(false)
    const [error, setError] = useState(null)
    const [selectedCat, setSelectedCat] = useState(location.state?.selectedCat || 'all')
    const [rowSelection, setRowSelection] = useState({})

    // Estados para los modales
    const [showAsignarModal, setShowAsignarModal] = useState(false)
    const [showHistorialModal, setShowHistorialModal] = useState(false)
    const [showDarBajaModal, setShowDarBajaModal] = useState(false)
    const [selectedActivo, setSelectedActivo] = useState(null)
    const [motivoBaja, setMotivoBaja] = useState('')

    // Estados para filtrado

    const [activeFilter, setActiveFilter] = useState(location.state?.activeFilter || null)
    const [activeTab, setActiveTab] = useState('activos')
    const [reporteInventarioAbierto, setReporteInventarioAbierto] = useState(false)
    const [insumosCount, setInsumosCount] = useState(null)
    useEffect(() => {
        api.get('/consumibles').then((r) => setInsumosCount(Array.isArray(r.data) ? r.data.length : null)).catch(() => setInsumosCount(null))
    }, [])
    const [searchTerm, setSearchTerm] = useState('')
    // Filtros extra por categoría (aparecen debajo de las cards cuando hay
    // una categoría seleccionada) -- se resetean al cambiar de categoría.
    const [marcaFilter, setMarcaFilter] = useState('')
    const [estadoFilter, setEstadoFilter] = useState('')
    const [marcaOptions, setMarcaOptions] = useState([])

    // Opciones de marca disponibles para la categoría seleccionada
    useEffect(() => {
        if (selectedCat === 'all') {
            setMarcaOptions([])
            return
        }
        let cancelled = false
        activosAPI.getMarcas(selectedCat)
            .then(({ data }) => { if (!cancelled) setMarcaOptions(data) })
            .catch(() => { if (!cancelled) setMarcaOptions([]) })
        return () => { cancelled = true }
    }, [selectedCat])

    // Persistir estado en el historial de navegación (permite mantener filtros al volver atrás, pero resetea al recargar o cambiar de sección)
    useEffect(() => {
        const currentState = { selectedCat, activeFilter }
        const previousState = location.state

        // Solo actualizar si hubo cambios para evitar loops
        if (
            previousState?.selectedCat !== selectedCat ||
            JSON.stringify(previousState?.activeFilter) !== JSON.stringify(activeFilter)
        ) {
            navigate('.', { replace: true, state: currentState })
        }
    }, [selectedCat, activeFilter, navigate, location.state])

    // Sincronizar activeTab y filtros desde URL search params al navegar desde el sidebar
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        const catParam = searchParams.get('categoria');
        const filtroParam = searchParams.get('filtro');

        if (tabParam) {
            setActiveTab(tabParam === 'consumibles' ? 'consumibles' : 'activos');
        }
        if (catParam) {
            setSelectedCat(catParam);
            setActiveFilter({ type: 'categoria', value: catParam });
            setServerPage(1);
        } else if (filtroParam === 'sinAsignar') {
            setSelectedCat('all');
            setActiveFilter({ type: 'sinAsignar', value: 'Sin asignar' });
            setServerPage(1);
        } else if (tabParam === 'activos' && !catParam && !filtroParam) {
            setSelectedCat('all');
            setActiveFilter(null);
            setServerPage(1);
        }
    }, [location.search]);

    const [formEditar, setFormEditar] = useState(null) // código del activo que se edita en el modal
    const [formNuevo, setFormNuevo] = useState(null) // null = cerrado; si no, { categoria }

    // El menú lateral entra con ?nuevo=1: abre el formulario de alta como modal sobre el listado
    useEffect(() => {
        if (new URLSearchParams(location.search).get('nuevo') === '1') {
            setFormNuevo({ categoria: '' })
            navigate(location.pathname, { replace: true, state: location.state })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search])

    const fetchActivos = async (signal, page = serverPage, overrideFilters = {}) => {
        setLoading(true)
        setError(null)
        try {
            const params = new URLSearchParams()
            const cat = overrideFilters.categoria ?? (selectedCat !== 'all' ? selectedCat : undefined)
            const sinAsignar = overrideFilters.sinAsignar ?? (activeFilter?.type === 'sinAsignar' ? true : undefined)
            // 'Equipos' abarca múltiples categorías — dejar que el cliente filtre
            const isMultiCat = cat === 'Equipos'
            if (cat && !isMultiCat) params.set('categoria', cat)
            if (sinAsignar) params.set('sinAsignar', 'true')
            if (searchTerm) params.set('search', searchTerm)
            if (marcaFilter) params.set('marca', marcaFilter)
            if (estadoFilter) params.set('estado', estadoFilter)
            params.set('page', String(page))
            params.set('pageSize', '50')

            const url = `/activos${params.toString() ? `?${params.toString()}` : ''}`
            const { data } = await api.get(url, signal ? { signal } : {})
            setActivos(data.items ?? data)
            if (data.pagination) setServerPagination(data.pagination)
            if (data.stats) setServerStats(data.stats)
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') return
            console.error('Error al cargar activos:', err)
            setError('Error al cargar los activos. Verifica tu conexión.')
        } finally {
            hasLoadedOnce.current = true
            setLoading(false)
        }
    }

    // Efecto único: carga activos cuando cambia página, categoría, filtro o búsqueda
    useEffect(() => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => {
            fetchActivos(controller.signal, serverPage)
        }, 300)
        return () => {
            controller.abort()
            clearTimeout(timeoutId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverPage, selectedCat, activeFilter, searchTerm, marcaFilter, estadoFilter])

    // Los filtros de Marca/Estado son por categoría -- si el usuario cambia
    // de card, no tiene sentido arrastrar un filtro que puede ni existir en
    // la categoría nueva.
    useEffect(() => {
        setMarcaFilter('')
        setEstadoFilter('')
        setServerPage(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCat])

    // Calcular estadísticas por categoría (preferir datos del servidor cuando estén disponibles)
    const stats = useMemo(() => {
        if (serverStats) {
            const categoriasArray = Object.entries(serverStats.porCategoria)
                .sort(([, a], [, b]) => b - a)
                .map(([nombre, cantidad]) => ({ nombre, cantidad }))
            return {
                total: serverStats.total,
                porCategoria: serverStats.porCategoria,
                categoriasArray,
                sinAsignar: serverStats.sinAsignar,
            }
        }
        if (!activos.length) return { total: 0, porCategoria: {}, categoriasArray: [], sinAsignar: 0 }
        const porCategoria = activos.reduce((acc, activo) => {
            const categoria = activo.categoria || 'Sin categoría'
            acc[categoria] = (acc[categoria] || 0) + 1
            return acc
        }, {})
        const categoriasArray = Object.entries(porCategoria)
            .sort(([, a], [, b]) => b - a)
            .map(([nombre, cantidad]) => ({ nombre, cantidad }))
        const sinAsignar = activos.filter(activo => !activo.asignadoA || !activo.asignadoA.nombre).length
        return { total: activos.length, porCategoria, categoriasArray, sinAsignar }
    }, [activos, serverStats])

    // Obtener columnas según categoría seleccionada
    const columns = useMemo(() => {
        return columnsByCategory[selectedCat] || columnsByCategory.all
    }, [selectedCat])

    // Filtrar y mapear datos según categoría
    const data = useMemo(() => {
        let filteredActivos = activos

        // Aplicar filtro de "sin asignar" si está activo
        if (activeFilter?.type === 'sinAsignar') {
            filteredActivos = activos.filter(activo => !activo.asignadoA || !activo.asignadoA.nombre)
        }

        if (selectedCat === 'all') {
            return filteredActivos
        }
        if (selectedCat === 'Equipos') {
            const equiposFiltrados = filteredActivos
                .filter(a => EQUIPOS_CATEGORIAS.includes(a.categoria))
                .map(a => {
                    return {
                        ...a,
                        tipoEquipo: a.tipoEquipo || '',
                        nombre: a.nombreEquipo,
                        procesador: a.procesador || '',
                        ram: a.ram,
                        marca: a.marca,
                        modelo: a.modelo,
                        discos: a.discos || [],
                        ubicacion: a.ubicacion || '',
                    }
                })
            return equiposFiltrados
        }
        if (selectedCat === 'Móviles') {
            return filteredActivos
                .filter(a => a.categoria === 'Móviles')
                .map(a => ({
                    ...a,
                    // adapta los campos según tu modelo real
                }))
        }
        if (selectedCat === 'Monitores') {
            return filteredActivos
                .filter(a => a.categoria === 'Monitores')
                .map(a => ({
                    ...a,
                    // adapta los campos según tu modelo real
                }))
        }
        if (selectedCat === 'Periféricos') {
            return filteredActivos
                .filter(a => a.categoria === 'Periféricos')
                .map(a => ({
                    ...a,
                    // adapta los campos según tu modelo real
                }))
        }
        if (selectedCat === 'Accesorios') {
            return filteredActivos
                .filter(a => a.categoria === 'Accesorios')
                .map(a => ({
                    ...a,
                    // adapta los campos según tu modelo real
                }))
        }
        if (selectedCat === 'Red') {
            return filteredActivos
                .filter(a => a.categoria === 'Red')
                .map(a => ({
                    ...a,
                    // adapta los campos según tu modelo real
                }))
        }
        return filteredActivos
    }, [activos, selectedCat, activeFilter, EQUIPOS_CATEGORIAS])

    // A-3: Funciones de filtrado — siempre resetean a página 1 (React 18 batchea los setState)
    const handleSearchChange = useCallback((term) => {
        setServerPage(1)
        setSearchTerm(term)
    }, [])

    const handleFilterByCategory = useCallback((categoria) => {
        setServerPage(1)
        setActiveFilter(prev => {
            if (prev?.type === 'categoria' && prev?.value === categoria) {
                setSelectedCat('all')
                return null
            }
            setSelectedCat(categoria)
            return { type: 'categoria', value: categoria }
        })
    }, [])

    const handleFilterByTotal = useCallback(() => {
        setServerPage(1)
        setActiveFilter(null)
        setSelectedCat('all')
    }, [])

    const handleFilterBySinAsignar = useCallback(() => {
        setServerPage(1)
        setActiveFilter(prev => {
            if (prev?.type === 'sinAsignar') {
                setSelectedCat('all')
                return null
            }
            setSelectedCat('all')
            return { type: 'sinAsignar', value: 'Sin asignar' }
        })
    }, [])

    // A-6: memoize selection-derived values so they don't recreate on every render
    const { selectedRowIndex, selectedId, selectedCodigo, selectedCount } = useMemo(() => {
        const idx = Object.keys(rowSelection)[0]
        return {
            selectedRowIndex: idx,
            selectedId: idx !== undefined ? data[idx]?.id : undefined,
            selectedCodigo: idx !== undefined ? data[idx]?.codigo : undefined,
            selectedCount: Object.keys(rowSelection).length,
        }
    }, [rowSelection, data])

    const handleDelete = async () => {
        if (selectedCount === 0) return

        const confirmMessage = selectedCount === 1
            ? '¿Seguro que deseas eliminar este activo?'
            : `¿Seguro que deseas eliminar ${selectedCount} activos?`

        if (!window.confirm(confirmMessage)) return

        try {
            if (selectedCount === 1) {
                // Eliminación individual
                await api.delete(`/activos/${selectedId}`)
            } else {
                // Eliminación múltiple
                const codigosAEliminar = Object.keys(rowSelection).map(index => data[parseInt(index)].codigo)
                await api.delete('/activos/multiple', { data: codigosAEliminar })
            }

            await fetchActivos(undefined, serverPage) // refresca la lista desde el backend
            setRowSelection({})
        } catch (err) {
            console.error('Error al eliminar activo(s):', err)
        }
    }

    const handleAsignar = () => {
        if (selectedCount === 0) return

        if (selectedCount === 1) {
            // Asignación individual
            const activo = data[selectedRowIndex]
            setSelectedActivo(activo)
        } else {
            // Asignación múltiple
            const activosSeleccionados = Object.keys(rowSelection).map(index => data[parseInt(index)])
            setSelectedActivo(activosSeleccionados)
        }

        setShowAsignarModal(true)
    }

    const handleVerHistorial = () => {
        if (!selectedCodigo || selectedCount !== 1) return
        const activo = data[selectedRowIndex]
        setSelectedActivo(activo)
        setShowHistorialModal(true)
    }

    const handleAsignacionCreada = () => {
        fetchActivos(undefined, serverPage)
        setRowSelection({})
    }

    const handleDevolucionRealizada = () => {
        fetchActivos(undefined, serverPage)
    }

    const handleDarBaja = async () => {
        if (!motivoBaja.trim()) {
            showToast('Debe ingresar un motivo para dar de baja', 'error')
            return
        }

        try {
            const selectedActivos = Object.keys(rowSelection).map(index => activos[index])

            for (const activo of selectedActivos) {
                await activosAPI.darBaja(activo.id, motivoBaja)
            }

            showToast(`Activo(s) dado(s) de baja exitosamente`, 'success')
            setShowDarBajaModal(false)
            setMotivoBaja('')
            setRowSelection({})
            fetchActivos(undefined, serverPage)
        } catch (error) {
            console.error('Error al dar de baja:', error)
            const errorMessage = error.response?.data || 'Error al dar de baja el activo'
            showToast(errorMessage, 'error')
        }
    }

    // A-3: stable reference so React.memo(DataTable) isn't defeated
    const handleRowClick = useCallback((row) => {
        navigate(`/activos/${row.codigo}`)
    }, [navigate])

    // A-2: memoize actions JSX — only recalculates when selection or navigation change
    const actions = useMemo(() => (
        <div className="flex flex-col xl:flex-row gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={() => {
                        const categoriaSeleccionada = activeFilter?.value || (selectedCat !== 'all' ? selectedCat : '')
                        setFormNuevo({ categoria: categoriaSeleccionada })
                    }}
                    className="flex items-center space-x-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 rounded-lg transition-colors font-medium border border-transparent"
                >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Nuevo</span>
                </button>
                <button
                    onClick={() => {
                        if (selectedCodigo && selectedCount === 1) {
                            navigate(`/activos/${selectedCodigo}`);
                        }
                    }}
                    disabled={!selectedCodigo || selectedCount !== 1}
                    className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedCodigo && selectedCount === 1
                        ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        }`}
                >
                    <Eye className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Ver</span>
                    <span className="sm:hidden">Ver</span>
                </button>
                <button
                    onClick={() => {
                        if (selectedCodigo && selectedCount === 1) {
                            setFormEditar(selectedCodigo);
                        }
                    }}
                    disabled={!selectedCodigo || selectedCount !== 1}
                    className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedCodigo && selectedCount === 1
                        ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        }`}
                >
                    <Edit className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Editar</span>
                    <span className="sm:hidden">Edit</span>
                </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={handleAsignar}
                    disabled={selectedCount === 0}
                    className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedCount > 0
                        ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        }`}
                >
                    <User className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{selectedCount === 1 ? 'Asignar' : `Asignar (${selectedCount})`}</span>
                    <span className="sm:hidden">{selectedCount === 1 ? 'Asignar' : `(${selectedCount})`}</span>
                </button>
                <button
                    onClick={handleVerHistorial}
                    disabled={!selectedCodigo || selectedCount !== 1}
                    className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedCodigo && selectedCount === 1
                        ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        }`}
                >
                    <History className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Historial</span>
                    <span className="sm:hidden">Hist</span>
                </button>
                <button
                    onClick={() => setShowDarBajaModal(true)}
                    disabled={selectedCount === 0}
                    className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedCount > 0
                        ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        }`}
                >
                    <Archive className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{selectedCount === 1 ? 'Dar de Baja' : `Dar de Baja (${selectedCount})`}</span>
                    <span className="sm:hidden">{selectedCount === 1 ? 'Baja' : `Baja (${selectedCount})`}</span>
                </button>
                <button
                    onClick={handleDelete}
                    disabled={selectedCount === 0}
                    className={`text-xs px-3 py-2 rounded-lg transition-colors font-medium flex items-center space-x-1.5 border ${selectedCount > 0
                        ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        }`}
                >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{selectedCount === 1 ? 'Borrar' : `Borrar (${selectedCount})`}</span>
                    <span className="sm:hidden">{selectedCount === 1 ? 'Borrar' : `(${selectedCount})`}</span>
                </button>
            </div>
        </div>
    ), [selectedCount, selectedCodigo, selectedRowIndex, navigate, handleAsignar, handleVerHistorial, handleDelete, activeFilter, selectedCat])

    if (loading && !hasLoadedOnce.current) {
        return (
            <div className="p-6">
                <h1 className="text-2xl font-semibold flex items-center text-gray-900 dark:text-white">
                    <HardDrive className="w-6 h-6 mr-2 text-primary" />
                    Activos
                </h1>
                <p className="mt-4 text-gray-700 dark:text-gray-300">Cargando activos...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <h1 className="text-2xl font-semibold flex items-center text-gray-900 dark:text-white">
                    <HardDrive className="w-6 h-6 mr-2 text-primary" />
                    Activos
                </h1>
                <div className="mt-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 rounded">
                    <p>{error}</p>
                    <button
                        onClick={() => fetchActivos(undefined, serverPage)}
                        className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-2 sm:p-6 space-y-4 max-w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h1 className="text-xl sm:text-2xl font-semibold flex items-center text-gray-900 dark:text-white">
                    {activeTab === 'activos' ? (
                        <>
                            <HardDrive className="w-6 h-6 mr-2 text-primary" />
                            Gestión de Activos
                        </>
                    ) : (
                        <>
                            <Package className="w-6 h-6 mr-2 text-blue-600" />
                            Gestión de Insumos
                        </>
                    )}
                </h1>

                <div className="flex items-center gap-3">
                {['admin', 'soporte'].includes(authUser?.role?.toLowerCase()) && (
                    <button
                        type="button"
                        onClick={() => setReporteInventarioAbierto(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <FileBarChart size={16} /> Reporte de inventario
                    </button>
                )}
                <div className="flex p-1 space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <button
                        onClick={() => setActiveTab('activos')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'activos'
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                    >
                        Activos Fijos
                    </button>
                    <button
                        onClick={() => setActiveTab('consumibles')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${activeTab === 'consumibles'
                            ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            }`}
                    >
                        Insumos
                    </button>
                </div>
                </div>
                <ReporteInventarioModal open={reporteInventarioAbierto} onClose={() => setReporteInventarioAbierto(false)} />
            </div>

            {activeTab === 'consumibles' ? (
                <Consumibles embedded={true} />
            ) : (
                <>

                    {/* Cards de estadísticas */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 sm:gap-4 mb-6">
                        {/* Total de activos */}
                        <div
                            className={`bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent ${activeFilter === null ? 'ring-2 ring-blue-500' : ''}`}
                            onClick={handleFilterByTotal}
                        >
                            <div className="p-3 rounded-lg text-blue-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <List className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Activos</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                            </div>
                        </div>

                        {/* Sin Asignar */}
                        <div
                            className={`bg-gray-100 dark:bg-gray-800 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent ${activeFilter?.type === 'sinAsignar' ? 'ring-2 ring-gray-500' : ''}`}
                            onClick={() => handleFilterBySinAsignar()}
                        >
                            <div className="p-3 rounded-lg text-gray-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <AlertTriangle className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sin Asignar</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.sinAsignar || 0}</p>
                            </div>
                        </div>

                        {/* Equipos */}
                        <div
                            className={`bg-green-50 dark:bg-green-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent ${activeFilter?.type === 'categoria' && activeFilter?.value === 'Equipos' ? 'ring-2 ring-green-500' : ''}`}
                            onClick={() => handleFilterByCategory('Equipos')}
                        >
                            <div className="p-3 rounded-lg text-green-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <HardDrive className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Equipos</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{
                                    EQUIPOS_CATEGORIAS.reduce((acc, cat) => acc + (stats.porCategoria[cat] || 0), 0)
                                }</p>
                            </div>
                        </div>

                        {/* Móviles */}
                        <div
                            className={`bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent ${activeFilter?.type === 'categoria' && activeFilter?.value === 'Móviles' ? 'ring-2 ring-purple-500' : ''}`}
                            onClick={() => handleFilterByCategory('Móviles')}
                        >
                            <div className="p-3 rounded-lg text-purple-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <Smartphone className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Móviles</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.porCategoria['Móviles'] || 0}</p>
                            </div>
                        </div>

                        {/* Monitores */}
                        <div
                            className={`bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent ${activeFilter?.type === 'categoria' && activeFilter?.value === 'Monitores' ? 'ring-2 ring-orange-500' : ''}`}
                            onClick={() => handleFilterByCategory('Monitores')}
                        >
                            <div className="p-3 rounded-lg text-orange-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <Monitor className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Monitores</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.porCategoria['Monitores'] || 0}</p>
                            </div>
                        </div>

                        {/* Redes y Comunicaciones */}
                        <div
                            className={`bg-cyan-50 dark:bg-cyan-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent ${activeFilter?.type === 'categoria' && activeFilter?.value === 'Red' ? 'ring-2 ring-cyan-500' : ''}`}
                            onClick={() => handleFilterByCategory('Red')}
                        >
                            <div className="p-3 rounded-lg text-cyan-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <Wifi className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Redes</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.porCategoria['Red'] || 0}</p>
                            </div>
                        </div>

                        {/* Insumos: abre la pestaña de insumos */}
                        <div
                            className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all hover:scale-105 border border-transparent"
                            onClick={() => setActiveTab('consumibles')}
                            role="button"
                            title="Ir a Insumos"
                        >
                            <div className="p-3 rounded-lg text-amber-600 bg-white/60 dark:bg-black/10 flex-shrink-0">
                                <Package className="h-6 w-6" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Insumos</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{insumosCount ?? '—'}</p>
                            </div>
                        </div>

                    </div>

                    {/* Indicador de filtro activo */}
                    {activeFilter && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                    <strong>Filtro activo:</strong> {activeFilter.type === 'categoria' ? `Categoría - ${activeFilter.value}` : activeFilter.value}
                                </p>
                                <button
                                    onClick={() => {
                                        setServerPage(1)
                                        setActiveFilter(null)
                                        setSelectedCat('all')
                                    }}
                                    className="text-blue-500 hover:text-blue-700 underline text-sm"
                                >
                                    Limpiar filtro
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Filtros extra por categoría: Marca y Estado -- solo tiene
                        sentido mostrarlos cuando hay una card de categoría
                        seleccionada (con "Todos" mezclando categorías muy
                        distintas, un filtro de marca no ayuda mucho). */}
                    {selectedCat !== 'all' && (
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            {marcaOptions.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Marca</label>
                                    <select
                                        value={marcaFilter}
                                        onChange={(e) => { setServerPage(1); setMarcaFilter(e.target.value) }}
                                        className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Todas</option>
                                        {marcaOptions.map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Estado</label>
                                <select
                                    value={estadoFilter}
                                    onChange={(e) => { setServerPage(1); setEstadoFilter(e.target.value) }}
                                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">Todos</option>
                                    <option value="Asignado">Asignado</option>
                                    <option value="Disponible">Disponible</option>
                                    <option value="Activo">Activo</option>
                                    <option value="En Mantenimiento">En Mantenimiento</option>
                                    <option value="Dado de Baja">Dado de Baja</option>
                                    <option value="Retirado">Retirado</option>
                                </select>
                            </div>
                            {(marcaFilter || estadoFilter) && (
                                <button
                                    onClick={() => { setMarcaFilter(''); setEstadoFilter(''); setServerPage(1) }}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Limpiar filtros de marca/estado
                                </button>
                            )}
                        </div>
                    )}

                    {/* Tabla con acciones y columnas dinámicas */}
                    <DataTable
                        columns={columns}
                        data={data}
                        enableRowSelection
                        rowSelection={rowSelection}
                        onRowSelectionChange={setRowSelection}
                        onRowClick={handleRowClick}
                        actions={actions}
                        onSearchChange={handleSearchChange}
                        hideClientPagination={serverPagination.totalPages > 1}
                    />

                    {/* Paginación server-side */}
                    {serverPagination.totalPages > 1 && (
                        <div className="flex items-center justify-between mt-3 px-1">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Mostrando {((serverPagination.page - 1) * serverPagination.pageSize) + 1}–{Math.min(serverPagination.page * serverPagination.pageSize, serverPagination.totalCount)} de {serverPagination.totalCount} activos
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setServerPage(1)}
                                    disabled={serverPage <= 1}
                                    className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    title="Primera página"
                                >«</button>
                                <button
                                    onClick={() => setServerPage(p => Math.max(1, p - 1))}
                                    disabled={serverPage <= 1}
                                    className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    title="Página anterior"
                                >‹</button>
                                <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                    {serverPagination.page} / {serverPagination.totalPages}
                                </span>
                                <button
                                    onClick={() => setServerPage(p => Math.min(serverPagination.totalPages, p + 1))}
                                    disabled={serverPage >= serverPagination.totalPages}
                                    className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    title="Página siguiente"
                                >›</button>
                                <button
                                    onClick={() => setServerPage(serverPagination.totalPages)}
                                    disabled={serverPage >= serverPagination.totalPages}
                                    className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    title="Última página"
                                >»</button>
                            </div>
                        </div>
                    )}

                    {/* Modales */}
                    <AsignarActivoModal
                        isOpen={showAsignarModal}
                        onClose={() => setShowAsignarModal(false)}
                        activo={selectedActivo}
                        onAsignacionCreada={handleAsignacionCreada}
                    />

                    <HistorialAsignacionesModal
                        isOpen={showHistorialModal}
                        onClose={() => setShowHistorialModal(false)}
                        activo={selectedActivo}
                        onDevolucionRealizada={handleDevolucionRealizada}
                    />

                    {/* Modal para dar de baja */}
                    {showDarBajaModal && createPortal(
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
                            onClick={(e) => {
                                if (e.target === e.currentTarget) {
                                    setShowDarBajaModal(false);
                                    setMotivoBaja('');
                                }
                            }}
                        >
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-4 sm:p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                        <AlertTriangle className="w-5 h-5 mr-2 text-orange-500" />
                                        Dar de Baja Activo(s)
                                    </h2>
                                    <button
                                        onClick={() => {
                                            setShowDarBajaModal(false)
                                            setMotivoBaja('')
                                        }}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        ¿Está seguro que desea dar de baja {selectedCount === 1 ? 'el activo seleccionado' : `los ${selectedCount} activos seleccionados`}?
                                        Esta acción no se puede deshacer.
                                    </p>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Motivo de la baja *
                                        </label>
                                        <textarea
                                            value={motivoBaja}
                                            onChange={(e) => setMotivoBaja(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Ingrese el motivo de la baja..."
                                            rows="3"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                                    <button
                                        onClick={() => {
                                            setShowDarBajaModal(false)
                                            setMotivoBaja('')
                                        }}
                                        className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors rounded-lg"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleDarBaja}
                                        disabled={!motivoBaja.trim()}
                                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Confirmar Baja
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}
                </>
            )}
            {formEditar && (
                <ActivosForm
                    modal
                    edit
                    idProp={formEditar}
                    onClose={() => setFormEditar(null)}
                    onSaved={() => fetchActivos(undefined, serverPage)}
                />
            )}
            {formNuevo && (
                <ActivosForm
                    modal
                    categoriaInicial={formNuevo.categoria}
                    onClose={() => setFormNuevo(null)}
                    onSaved={() => fetchActivos(undefined, serverPage)}
                />
            )}
        </div>
    )
}
